import { Device } from 'mediasoup-client';

// Sin STUN/TURN configurado, cualquier
// cliente detrás de NAT simétrico o un firewall corporativo no puede establecer el canal
// WebRTC. STUN público como primer intento (gratis, sin relay); TURN (coturn, credenciales
// efímeras del backend) como fallback cuando el directo falla.
const PUBLIC_STUN_SERVER = { urls: 'stun:stun.l.google.com:19302' };

class MediasoupManager {
    constructor(socket, onConsume) {
        this.socket = socket;
        this.device = null;
        this.sendTransport = null;
        this.recvTransport = null;
        this.audioProducer = null;
        this.videoProducer = null;
        // Migración del screen share desde el mesh P2P legado (.claude/plans/mejoras-futuras.md,
        // punto 6, decisión del usuario 2026-07-13): mismo patrón que audioProducer/videoProducer.
        // Separado en dos producers (video + audio opcional) porque `getDisplayMedia({audio:true})`
        // puede o no traer pista de audio según lo que el usuario marque en el picker nativo.
        this.screenVideoProducer = null;
        this.screenAudioProducer = null;
        // sevilla-fix-comunication: antes la clave era solo `userId` -- funcionaba mientras
        // cada usuario tenía a lo más UN producer (audio). Al agregar video, un mismo usuario
        // puede tener consumers de audio Y video simultáneos; con la clave vieja, consumir el
        // segundo pisaba la entrada del primero en este Map (y en `consuming`), perdiendo la
        // referencia para pausar/cerrar/reanudar correctamente. Clave compuesta `userId:kind`.
        this.consumers = new Map(); // "userId:kind" -> consumer object
        this.consuming = new Set(); // "userId:kind" -> boolean (lock)
        this.onConsume = onConsume; // callback(userId, track, kind)
        this._rotationTimer = null;
        this._destroyed = false;

        // Bug encontrado (2026-07-08): antes este listener se registraba DENTRO
        // de consume(), una vez por cada llamada — en uso normal (usuarios entrando/saliendo
        // del radio de proximidad todo el tiempo), esto acumulaba miles de listeners nunca
        // removidos a lo largo de una sesión larga. Se registra una sola vez acá, y busca el
        // consumer correspondiente iterando el Map en el momento del evento.
        // Además, `destroy()` nunca hacía `socket.off()` de este listener — como `socket`
        // es la conexión singleton compartida
        // de toda la app (no se cierra al destruir el manager), cada instancia nueva de
        // `MediasoupManager` (Map.jsx la recreaba en cada edición de perfil) dejaba un
        // listener viejo vivo para siempre. Se guarda la referencia para poder quitarla.
        this._onConsumerClosed = ({ consumerId }) => {
            for (const [key, consumer] of this.consumers) {
                if (consumer.id === consumerId) {
                    consumer.close();
                    this.consumers.delete(key);
                    break;
                }
            }
        };
        this.socket.on('consumer-closed', this._onConsumerClosed);
    }

    async init() {
        // 1. Get Router RTP Capabilities
        const routerRtpCapabilities = await new Promise((resolve, reject) => {
            this.socket.emit('getRouterRtpCapabilities', (res) => {
                if (res.error) reject(res.error);
                resolve(res);
            });
        });

        // 2. Initialize Device
        this.device = new Device();
        await this.device.load({ routerRtpCapabilities });

        // 3. Credenciales TURN — antes de crear los transports, porque
        // mediasoup-client solo acepta iceServers en el momento de creación del transport.
        const iceServers = await this._fetchIceServers();

        // 4. Create Send Transport
        this.sendTransport = await this._createSendTransport(iceServers);

        // 5. Create Receive Transport
        this.recvTransport = await this._createRecvTransport(iceServers);

        this._scheduleRotation();
    }

    async _fetchIceServers() {
        const res = await new Promise((resolve) => {
            this.socket.emit('getTurnCredentials', resolve);
        });

        if (!res || res.error) {
            // Sin TURN configurado (o el backend no tiene TURN_SHARED_SECRET) — degradar a
            // solo STUN en vez de bloquear la conexión. El camino directo sigue funcionando
            // igual; solo se pierde el fallback de relay.
            this._turnTtlSeconds = null;
            return [PUBLIC_STUN_SERVER];
        }

        this._turnTtlSeconds = res.ttlSeconds;
        return [PUBLIC_STUN_SERVER, { urls: res.urls, username: res.username, credential: res.credential }];
    }

    // Rotación de transports (decisión del usuario, 2026-07-08): las credenciales TURN
    // vencen a la hora, pero una pestaña del mapa puede quedar abierta todo el día
    // (Map.jsx nunca se desmonta en uso normal). mediasoup-client no permite "actualizar"
    // iceServers en un transport ya creado, así que antes de que venzan se crean transports
    // NUEVOS con credenciales frescas, se migra el audio en curso, y se cierran los viejos —
    // sin cortar la llamada. Se dispara al 75% del TTL (45min de una hora) para dejar margen.
    _scheduleRotation() {
        if (!this._turnTtlSeconds) return; // sin TURN configurado, no hay nada que rotar
        clearTimeout(this._rotationTimer);
        this._rotationTimer = setTimeout(() => {
            this._rotateTransports().catch((err) => console.error('[MediasoupManager] Rotación de TURN falló:', err));
        }, this._turnTtlSeconds * 1000 * 0.75);
    }

    async _rotateTransports() {
        if (this._destroyed) return;

        const iceServers = await this._fetchIceServers();
        const newSendTransport = await this._createSendTransport(iceServers);
        const newRecvTransport = await this._createRecvTransport(iceServers);

        // Migrar el audio que estoy transmitiendo (si hay) al transport nuevo ANTES de
        // cerrar el viejo, reusando el mismo track de micrófono (no se vuelve a pedir
        // getUserMedia). En cuanto se asigna `this.audioProducer`, el próximo heartbeat de
        // Map.jsx (que lee `audioProducer?.id` en cada tick) ya anuncia el producerId nuevo
        // — no hace falta tocar Map.jsx para que los demás se vuelvan a suscribir.
        if (this.audioProducer && !this.audioProducer.closed) {
            const oldProducer = this.audioProducer;
            const newProducer = await newSendTransport.produce({ track: oldProducer.track });
            this._wireAudioProducer(newProducer);
            this.audioProducer = newProducer;

            // Cerrar el producer viejo dispara 'producerclose' en cada consumer remoto que
            // lo tenía — el backend ya les manda 'consumer-closed' automáticamente (código
            // existente en src/shared/mediasoup.js), y su propio loop de proximidad los
            // vuelve a consumir usando el producerId nuevo en el próximo heartbeat.
            this.socket.emit('closeProducer', { producerId: oldProducer.id }, () => {});
            oldProducer.close();
        }

        // Mismo criterio que el audio de arriba, para el producer de cámara.
        if (this.videoProducer && !this.videoProducer.closed) {
            const oldVideoProducer = this.videoProducer;
            const newVideoProducer = await newSendTransport.produce({ track: oldVideoProducer.track });
            newVideoProducer.on('trackended', () => this.stopVideo());
            newVideoProducer.on('transportclose', () => this.stopVideo());
            this.videoProducer = newVideoProducer;

            this.socket.emit('closeProducer', { producerId: oldVideoProducer.id }, () => {});
            oldVideoProducer.close();
        }

        // Mismo criterio, para los producers de screen share (si hay uno activo al momento
        // de la rotación de TURN).
        if (this.screenVideoProducer && !this.screenVideoProducer.closed) {
            const oldScreenVideoProducer = this.screenVideoProducer;
            const newScreenVideoProducer = await newSendTransport.produce({ track: oldScreenVideoProducer.track });
            newScreenVideoProducer.on('trackended', () => this.stopScreenShare());
            newScreenVideoProducer.on('transportclose', () => this.stopScreenShare());
            this.screenVideoProducer = newScreenVideoProducer;

            this.socket.emit('closeProducer', { producerId: oldScreenVideoProducer.id }, () => {});
            oldScreenVideoProducer.close();
        }
        if (this.screenAudioProducer && !this.screenAudioProducer.closed) {
            const oldScreenAudioProducer = this.screenAudioProducer;
            const newScreenAudioProducer = await newSendTransport.produce({ track: oldScreenAudioProducer.track });
            newScreenAudioProducer.on('transportclose', () => this.stopScreenShare());
            this.screenAudioProducer = newScreenAudioProducer;

            this.socket.emit('closeProducer', { producerId: oldScreenAudioProducer.id }, () => {});
            oldScreenAudioProducer.close();
        }

        // Swap del recvTransport ANTES de cerrar el viejo: cerrar el viejo transport cierra
        // en cascada todos sus consumers (evento 'transportclose', ya manejado más abajo),
        // y el loop de proximidad de Map.jsx — que corre en cada 'map-update', varias veces
        // por segundo — los vuelve a consumir solo si `this.recvTransport` ya apunta al
        // nuevo cuando eso pasa.
        const oldSendTransport = this.sendTransport;
        const oldRecvTransport = this.recvTransport;
        this.sendTransport = newSendTransport;
        this.recvTransport = newRecvTransport;

        oldRecvTransport.close();
        oldSendTransport.close();

        this._scheduleRotation();
    }

    _wireAudioProducer(producer) {
        producer.on('trackended', () => {
            console.log('Audio track ended');
            this.stopAudio();
        });
        producer.on('transportclose', () => {
            console.log('Audio transport closed');
            this.stopAudio();
        });
    }

    async _createSendTransport(iceServers) {
        const params = await new Promise((resolve, reject) => {
            this.socket.emit('createWebRtcTransport', (res) => {
                if (res.error) reject(res.error);
                resolve(res.params);
            });
        });

        const transport = this.device.createSendTransport({ ...params, iceServers });

        transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
            try {
                await new Promise((resolve, reject) => {
                    this.socket.emit('connectWebRtcTransport', {
                        transportId: transport.id,
                        dtlsParameters
                    }, (res) => {
                        if (res.error) reject(res.error);
                        resolve();
                    });
                });
                callback();
            } catch (error) {
                errback(error);
            }
        });

        transport.on('produce', async (parameters, callback, errback) => {
            try {
                const id = await new Promise((resolve, reject) => {
                    this.socket.emit('produce', {
                        transportId: transport.id,
                        kind: parameters.kind,
                        rtpParameters: parameters.rtpParameters
                    }, (res) => {
                        if (res.error) reject(res.error);
                        resolve(res.id);
                    });
                });
                callback({ id });
            } catch (error) {
                errback(error);
            }
        });

        return transport;
    }

    async _createRecvTransport(iceServers) {
        const params = await new Promise((resolve, reject) => {
            this.socket.emit('createWebRtcTransport', (res) => {
                if (res.error) reject(res.error);
                resolve(res.params);
            });
        });

        const transport = this.device.createRecvTransport({ ...params, iceServers });

        transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
            try {
                await new Promise((resolve, reject) => {
                    this.socket.emit('connectWebRtcTransport', {
                        transportId: transport.id,
                        dtlsParameters
                    }, (res) => {
                        if (res.error) reject(res.error);
                        resolve();
                    });
                });
                callback();
            } catch (error) {
                errback(error);
            }
        });

        return transport;
    }

    async publishAudio(track) {
        if (!this.sendTransport) throw new Error("Send transport not initialized");

        this.audioProducer = await this.sendTransport.produce({ track });
        this._wireAudioProducer(this.audioProducer);

        return this.audioProducer.id;
    }

    stopAudio() {
        if (this.audioProducer) {
            this.socket.emit('closeProducer', { producerId: this.audioProducer.id }, () => {});
            this.audioProducer.close();
            this.audioProducer = null;
        }
    }

    // sevilla-fix-comunication: mismo patrón que publishAudio/stopAudio -- el botón de cámara
    // (mediaActions.cam) existía en la UI pero no tenía ningún producer real detrás.
    //
    // `encodings` con un solo `maxBitrate` -- antes no había ningún techo real de bitrate
    // (`x-google-start-bitrate` del router es solo un hint de arranque del encoder, no un
    // límite). Un solo encoding (no simulcast) mantiene el algoritmo nativo de adaptación por
    // ancho de banda de mediasoup (confirmado activo por defecto en el worker C++) sin sumar
    // el costo de CPU de codificar varias capas simultáneas.
    async publishVideo(track) {
        if (!this.sendTransport) throw new Error("Send transport not initialized");

        this.videoProducer = await this.sendTransport.produce({
            track,
            encodings: [{ maxBitrate: 800_000 }],
        });
        this.videoProducer.on('trackended', () => this.stopVideo());
        this.videoProducer.on('transportclose', () => this.stopVideo());

        return this.videoProducer.id;
    }

    stopVideo() {
        if (this.videoProducer) {
            this.socket.emit('closeProducer', { producerId: this.videoProducer.id }, () => {});
            this.videoProducer.close();
            this.videoProducer = null;
        }
    }

    // Migración del screen share al mismo SFU que audio/cámara (antes: mesh P2P manual,
    // `createPeerConnection`/`peersRef` en Map.jsx, sin TURN de verdad). `stream` es el
    // `MediaStream` completo de `getDisplayMedia` -- publica el track de video siempre, y el de
    // audio solo si el usuario lo marcó en el picker nativo del navegador (getDisplayMedia con
    // audio:true no garantiza una pista de audio real).
    async publishScreenShare(stream) {
        if (!this.sendTransport) throw new Error("Send transport not initialized");

        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];

        if (videoTrack) {
            // Mismo criterio de bitrate acotado que publishVideo -- ver comentario ahí.
            this.screenVideoProducer = await this.sendTransport.produce({
                track: videoTrack,
                encodings: [{ maxBitrate: 800_000 }],
            });
            this.screenVideoProducer.on('trackended', () => this.stopScreenShare());
            this.screenVideoProducer.on('transportclose', () => this.stopScreenShare());
        }
        if (audioTrack) {
            this.screenAudioProducer = await this.sendTransport.produce({ track: audioTrack });
            this.screenAudioProducer.on('transportclose', () => this.stopScreenShare());
        }

        return {
            videoProducerId: this.screenVideoProducer?.id || null,
            audioProducerId: this.screenAudioProducer?.id || null,
        };
    }

    stopScreenShare() {
        if (this.screenVideoProducer) {
            this.socket.emit('closeProducer', { producerId: this.screenVideoProducer.id }, () => {});
            this.screenVideoProducer.close();
            this.screenVideoProducer = null;
        }
        if (this.screenAudioProducer) {
            this.socket.emit('closeProducer', { producerId: this.screenAudioProducer.id }, () => {});
            this.screenAudioProducer.close();
            this.screenAudioProducer = null;
        }
    }

    async consume(userId, producerId, kind = 'audio') {
        const key = `${userId}:${kind}`;
        // Integración main→dev, epic C3 (`fd483bc8` / consumer-lifecycle): si ya hay un consumer
        // para esta clave pero el productor cambió o el consumer quedó cerrado, se limpia y se
        // vuelve a consumir en vez de retornar en silencio (era la causa de que un track de
        // audio se "perdiera" tras reconectar el productor).
        const existing = this.consumers.get(key);
        if (existing) {
            if (existing.producerId === producerId && !existing.closed) return; // Already consuming this exact, active producer
            try { existing.close(); } catch { /* ya cerrado */ }
            this.consumers.delete(key);
        }
        if (this.consuming.has(key)) return; // In progress
        this.consuming.add(key);
        if (!this.recvTransport) {
            this.consuming.delete(key);
            return;
        }

        // Bug encontrado (2026-07-08): sin try/finally, un error a mitad de
        // camino (ej. el transport se cerró por la rotación de TURN justo en este instante)
        // dejaba `userId` marcado en `consuming` para siempre — bloqueaba cualquier reintento
        // futuro de consumirlo, incluso después de que la condición que causó el error ya no
        // aplicara.
        try {
            const { rtpCapabilities } = this.device;

            const params = await new Promise((resolve, reject) => {
                this.socket.emit('consume', {
                    transportId: this.recvTransport.id,
                    producerId,
                    rtpCapabilities
                }, (res) => {
                    if (res.error) reject(res.error);
                    resolve(res.params);
                });
            });

            const consumer = await this.recvTransport.consume({
                id: params.id,
                producerId: params.producerId,
                kind: params.kind,
                rtpParameters: params.rtpParameters
            });

            this.consumers.set(key, consumer);

            // Resume on server
            this.socket.emit('resumeConsumer', { consumerId: consumer.id }, () => {});

            // Callback con el `kind` que pidió el caller (etiqueta de propósito propia:
            // 'audio'|'video'|'screen'|'screenAudio'), NO `params.kind` (el kind RTP crudo que
            // devuelve el servidor). A nivel RTP, un screen share de video es 'video' igual que
            // la cámara -- usar `params.kind` acá colisionaría ambos en la misma rama del
            // callback de Map.jsx. `params.kind` ya se usó arriba solo para configurar el
            // `consumer` de mediasoup-client correctamente.
            if (this.onConsume) {
                this.onConsume(userId, consumer.track, kind);
            }

            consumer.on('transportclose', () => {
                this.consumers.delete(key);
            });
            // Integración main→dev, epic C3: cerrar y soltar el consumer cuando el productor
            // remoto desaparece o el track se corta, para que un `consume()` posterior con el
            // mismo `key` no choque contra un consumer muerto.
            consumer.on('producerclose', () => {
                try { consumer.close(); } catch { /* noop */ }
                this.consumers.delete(key);
            });
            consumer.on('trackended', () => {
                try { consumer.close(); } catch { /* noop */ }
                this.consumers.delete(key);
            });
        } finally {
            this.consuming.delete(key);
        }
    }

    pauseConsumer(userId, kind = 'audio') {
        const consumer = this.consumers.get(`${userId}:${kind}`);
        if (consumer && !consumer.paused) {
            consumer.pause();
            this.socket.emit('pauseConsumer', { consumerId: consumer.id }, () => {});
        }
    }

    resumeConsumer(userId, kind = 'audio') {
        const consumer = this.consumers.get(`${userId}:${kind}`);
        if (consumer && consumer.paused) {
            consumer.resume();
            this.socket.emit('resumeConsumer', { consumerId: consumer.id }, () => {});
        }
    }

    closeConsumer(userId, kind = 'audio') {
        const key = `${userId}:${kind}`;
        const consumer = this.consumers.get(key);
        if (consumer) {
            this.socket.emit('closeConsumer', { consumerId: consumer.id }, () => {});
            try { consumer.close(); } catch { /* ya cerrado */ }
            this.consumers.delete(key);
        }
    }

    destroy() {
        this._destroyed = true;
        clearTimeout(this._rotationTimer);
        this.socket.off('consumer-closed', this._onConsumerClosed);
        this.stopAudio();
        this.stopVideo();
        this.stopScreenShare();
        if (this.sendTransport) this.sendTransport.close();
        if (this.recvTransport) this.recvTransport.close();
    }
}

export default MediasoupManager;
